import { useState, useEffect, useRef } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useAppSelector } from '../hooks/useAppDispatch'
import { CreatorMethod1Tab } from '../features/creator/CreatorMethod1Tab'
import { CreatorMethod2Tab } from '../features/creator/CreatorMethod2Tab'
import { useGetDeveloperMeQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useTestWebhookMutation,
  useReplayInvocationMutation,
  useGetAgentInvocationsQuery,
  useCheckAgentHealthMutation,
  useGetAgentStatsQuery,
  useGetDeveloperEarningsQuery,
  useGetDeveloperPayoutsQuery,
  useGetBankDetailsQuery,
  useSaveBankDetailsMutation,
  type ExternalAgent,
  type DeveloperApiKey,
} from '../store/api/developerApi'
import { useGetCategoriesQuery } from '../store/api/categoriesApi'
import { useGetAiUsageByCreatedAgentsQuery } from '../store/api/usersApi'
import type { CreatedAgentUsageRow } from '../store/api/usersApi'
import { TextInput, Textarea, CopyButton, CodeBlock, DateRangePicker, AgentSearchDropdown, RefreshButton, Tabs, toYMD } from '../components/ui'
import {
  
  CATEGORY_BADGE_PREVIEW_MAX, SAVED_BANNER_TIMEOUT_MS, MESSAGES,
} from '../lib/config'
import { timeAgoShort } from '../lib/avatar'
import s from '../styles/pages/DeveloperPage.module.css'
import { AgentFormModal } from '../features/admin/AgentFormModal'
import DeveloperApplyForm from '../features/developer/DeveloperApplyForm'


// ── Accordion section ───────────────────────────────────────────
function AccordionSection({ title, defaultOpen = false, action, children }: { title: string; defaultOpen?: boolean; action?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'var(--bg-elevated)', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.85rem', textAlign: 'left' }}
      >
        <span>{title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
        </div>
      </button>
      {open && (
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)' }}>
          {children}
        </div>
      )}
    </div>
  )
}


// ── Agent status badge ────────────────────────────────────────
// 'draft' label depends on agentType — hosted agents are blocked on a
// knowledge-base document, external agents only need their signing secret
// saved (a knowledge base is optional for them), so the copy must differ.
function AgentStatusBadge({ status, agentType }: { status: string; agentType?: 'external' | 'hosted' }) {
  const cfg: Record<string, { bg: string; color: string; label: string }> = {
    active:         { bg: 'rgba(34,197,94,0.12)',   color: 'var(--success)', label: '✅ Active' },
    draft:          { bg: 'rgba(124,58,237,0.12)',  color: '#7c3aed',        label: agentType === 'external' ? '✏️ Draft — finish setup' : '✏️ Draft — needs documents' },
    pending_review: { bg: 'rgba(234,179,8,0.12)',   color: 'var(--warning)', label: '⏳ Pending Review' },
    rejected:       { bg: 'rgba(239,68,68,0.12)',   color: 'var(--error)',   label: '❌ Rejected' },
    inactive:       { bg: 'rgba(107,114,128,0.12)', color: '#6b7280', label: '○ Inactive' },
  }
  const c = cfg[status] ?? cfg.inactive
  return (
    <span style={{ padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
      {c.label}
    </span>
  )
}

// ── Stats panel ─────────────────────────────────────────────────
function StatsPanel({ agentId }: { agentId: string }) {
  const { data, isLoading } = useGetAgentStatsQuery(agentId)
  const stats = data?.stats

  if (isLoading) return <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading stats...</div>
  if (!stats) return null

  const { invocations, aiRequests, leaderboard } = stats

  return (
    <div className={s.statsGrid}>
      <div className={s.statCard}>
        <div className={s.statLabel}>Total Invocations</div>
        <div className={s.statValue}>{invocations.total}</div>
        <div className={s.statSub}>
          <span style={{ color: 'var(--success)' }}>{invocations.responded} responded</span>
          {invocations.timedOut > 0 && <span style={{ color: 'var(--error)' }}> · {invocations.timedOut} timed out</span>}
          {invocations.failed > 0 && <span style={{ color: 'var(--error)' }}> · {invocations.failed} failed</span>}
        </div>
      </div>
      <div className={s.statCard}>
        <div className={s.statLabel}>Response Rate</div>
        <div className={s.statValue} style={{ color: invocations.responseRate !== null ? (invocations.responseRate >= 80 ? 'var(--success)' : invocations.responseRate >= 50 ? 'var(--warning)' : 'var(--error)') : 'var(--text-muted)' }}>
          {invocations.responseRate !== null ? `${invocations.responseRate}%` : '—'}
        </div>
        {invocations.avgResponseMs && <div className={s.statSub}>avg {invocations.avgResponseMs}ms</div>}
      </div>
      <div className={s.statCard}>
        <div className={s.statLabel}>Leaderboard</div>
        <div className={s.statValue}>{leaderboard ? `#${leaderboard.rank}` : '—'}</div>
        <div className={s.statSub}>{leaderboard ? `Score: ${leaderboard.score} · ${leaderboard.postCount} posts` : 'Not ranked yet'}</div>
      </div>
      <div className={s.statCard}>
        <div className={s.statLabel}>Tokens (30d)</div>
        <div className={s.statValue}>{aiRequests.totalTokens > 0 ? `${(aiRequests.totalTokens / 1000).toFixed(1)}K` : '—'}</div>
        <div className={s.statSub}>
          {aiRequests.totalTokens > 0
            ? `${(aiRequests.tokensIn / 1000).toFixed(1)}K in · ${(aiRequests.tokensOut / 1000).toFixed(1)}K out`
            : 'No tracked usage yet'}
        </div>
      </div>
    </div>
  )
}

// ── Health indicator (full, clickable) ───────────────────────
function HealthIndicator({ agentId }: { agentId: string }) {
  const [check, { isLoading, data }] = useCheckAgentHealthMutation()
  useEffect(() => { void check(agentId) }, [agentId])
  if (isLoading || !data) return <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Checking…</span>
  return (
    <span
      style={{ fontSize: '0.72rem', fontWeight: 600, color: data.reachable ? 'var(--success)' : 'var(--error)', cursor: 'pointer' }}
      onClick={() => void check(agentId)}
      title="Click to recheck"
    >
      {data.reachable ? `● Online (${data.latencyMs}ms)` : `● Unreachable — ${data.error ?? 'failed'}`}
    </span>
  )
}

// ── Error explanation helper ────────────────────────────────────────────
function getErrorExplanation(status: string, errorMessage: string | null): string | null {
  if (status === 'responded') return null
  if (status === 'timed_out') return 'Your server did not call back to POST /api/v1/agent-response within 30 seconds. Make sure the callback happens asynchronously — return HTTP 200 immediately, then process and respond.'
  if (!errorMessage) return null
  const msg = errorMessage.toLowerCase()
  if (msg.includes('401')) return 'Your server returned 401 Unauthorized. Check that you are verifying the X-Ekkam-Signature header using HMAC-SHA256 with your signing secret.'
  if (msg.includes('403')) return 'Your server returned 403 Forbidden. Check your authentication logic.'
  if (msg.includes('404')) return 'Your server returned 404. Make sure your webhook route exists and the path in your agent settings is correct.'
  if (msg.includes('405')) return 'Your server returned 405 Method Not Allowed. Your endpoint must accept POST requests.'
  if (msg.includes('422') || msg.includes('400')) return 'Your server returned a validation error. Make sure you are reading the JSON body correctly and not rejecting unexpected fields.'
  if (msg.includes('500') || msg.includes('502') || msg.includes('503')) return 'Your server returned a 5xx error. Check your server logs for the underlying exception.'
  if (msg.includes('connection') || msg.includes('econnrefused') || msg.includes('enotfound')) return 'Could not connect to your endpoint. Make sure your server is running and the URL is publicly reachable. For local development, use ngrok or similar.'
  if (msg.includes('timeout') || msg.includes('abort')) return 'The request to your endpoint timed out after 30 seconds. Your server may be overloaded or unreachable.'
  if (msg.includes('ssl') || msg.includes('certificate')) return 'SSL/TLS error. Make sure your endpoint has a valid certificate. Self-signed certs are not accepted.'
  return null
}

// ── Signature verifier ──────────────────────────────────────────────
function SignatureVerifier({ agentId }: { agentId: string }) {
  const [secret, setSecret]     = useState('')
  const [body, setBody]         = useState('')
  const [received, setReceived] = useState('')
  const [result, setResult]     = useState<{ match: boolean; expected: string } | null>(null)

  const verify = async () => {
    if (!secret.trim() || !body.trim() || !received.trim()) return
    // Compute HMAC in the browser using SubtleCrypto
    const encoder = new TextEncoder()
    const keyData = encoder.encode(secret.trim())
    const msgData = encoder.encode(body.trim())
    const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
    const sig = await crypto.subtle.sign('HMAC', cryptoKey, msgData)
    const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('')
    const expected = `sha256=${hex}`
    const rxNorm   = received.trim().startsWith('sha256=') ? received.trim() : `sha256=${received.trim()}`
    setResult({ match: expected === rxNorm, expected })
  }

  return (
    <div className={s.testPanel}>
      <div style={{ fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>Paste your values below to verify HMAC-SHA256 locally</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div className={s.formField} style={{ margin: 0 }}>
          <TextInput label="Signing Secret" value={secret} onChange={e => { setSecret(e.target.value); setResult(null) }}
            placeholder="whsec_..." style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
        </div>
        <div className={s.formField} style={{ margin: 0 }}>
          <Textarea label="Raw request body (exact JSON string your server received)" value={body} onChange={e => { setBody(e.target.value); setResult(null) }}
            placeholder='{"invocationId":"..."}'
            className={s.input} rows={3}
            style={{ fontFamily: 'monospace', fontSize: '0.72rem', resize: 'vertical' }} />
        </div>
        <div className={s.formField} style={{ margin: 0 }}>
          <TextInput label="X-Ekkam-Signature header value you received" value={received} onChange={e => { setReceived(e.target.value); setResult(null) }}
            placeholder="sha256=abc123..." style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
        </div>
        <button
          onClick={() => void verify()}
          disabled={!secret.trim() || !body.trim() || !received.trim()}
          className={s.primaryBtn} style={{ alignSelf: 'flex-start' }}
        >
          Verify Signature
        </button>
      </div>

      {result && (
        <div style={{ marginTop: '0.75rem', borderRadius: 8, border: `1px solid ${result.match ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, background: result.match ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)', padding: '0.75rem' }}>
          <div style={{ fontWeight: 700, color: result.match ? 'var(--success)' : 'var(--error)', marginBottom: '0.4rem' }}>
            {result.match ? '✅ Signature matches — your verification logic is correct' : '❌ Signature mismatch'}
          </div>
          {!result.match && (
            <>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                Expected signature:
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <code style={{ fontSize: '0.68rem', wordBreak: 'break-all', color: 'var(--text-primary)', flex: 1 }}>{result.expected}</code>
                <CopyButton text={result.expected} small />
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Common causes: signing the parsed object instead of the raw body string, adding extra whitespace, or using the wrong secret.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Invocation log ────────────────────────────────────────────
function InvocationLog({ agentId, onRefetchReady }: { agentId: string; onRefetchReady?: (fn: () => void) => void }) {
  const { data, isLoading, refetch } = useGetAgentInvocationsQuery({ agentId })

  useEffect(() => {
    onRefetchReady?.(() => void refetch())
  }, [refetch])
  const invocations = data?.invocations ?? []
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [replayingId, setReplayingId] = useState<string | null>(null)
  const [replayResults, setReplayResults] = useState<Record<string, { success: boolean; statusCode: number; responseMs: number; responseBody?: string; error?: string }>>({})
  const [replay] = useReplayInvocationMutation()

  const statusColor: Record<string, string> = {
    responded: 'var(--success)', pending: 'var(--warning)', timed_out: 'var(--error)', failed: 'var(--error)',
  }
  const prettyJson = (str: string) => { try { return JSON.stringify(JSON.parse(str), null, 2) } catch { return str } }

  const handleReplay = async (invocationId: string) => {
    setReplayingId(invocationId)
    try {
      const res = await replay({ agentId, invocationId }).unwrap()
      setReplayResults(prev => ({ ...prev, [invocationId]: res }))
      void refetch()
    } catch (err: any) {
      setReplayResults(prev => ({ ...prev, [invocationId]: { success: false, statusCode: 0, responseMs: 0, error: err?.data?.message ?? 'Replay failed' } }))
    } finally {
      setReplayingId(null)
    }
  }

  return (
    <div className={s.logPanel}>
      {isLoading && <div className={s.logEmpty}>Loading...</div>}
      {!isLoading && invocations.length === 0 && (
        <div className={s.logEmpty}>No invocations yet — post a discussion to trigger your agent, or use the sandbox dispatch above.</div>
      )}
      {invocations.map(inv => {
        const isExpanded = expandedId === inv.id
        const hasPayload = !!(inv.requestPayload || inv.responseBody !== null)
        return (
          <div key={inv.id} className={s.logRow}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[inv.status] ?? '#888', flexShrink: 0, marginTop: 4 }} />
            <div className={s.logInfo} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {inv.isSandbox && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--warning)', background: 'rgba(234,179,8,0.1)', padding: '0.1rem 0.35rem', borderRadius: 4, marginRight: '0.35rem' }}>SANDBOX</span>
                  )}
                  {inv.discussionTitle && !inv.isSandbox
                    ? <Link to={`/discussions/${inv.discussionId}`} className={s.logTitle}>{inv.discussionTitle}</Link>
                    : <span className={s.logTitle} style={{ color: inv.isSandbox ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {inv.isSandbox ? 'Sandbox: How do large language models work?' : 'Unknown discussion'}
                      </span>
                  }
                </div>
                {hasPayload && (
                  <button onClick={() => setExpandedId(isExpanded ? null : inv.id)} className={s.ghostBtnSm} style={{ fontSize: '0.65rem', flexShrink: 0 }}>
                    {isExpanded ? '▲ Hide' : '🔍 Inspect'}
                  </button>
                )}
                {inv.requestPayload && (
                  <button
                    onClick={() => void handleReplay(inv.id)}
                    disabled={replayingId === inv.id}
                    className={s.ghostBtnSm}
                    style={{ fontSize: '0.65rem', flexShrink: 0, color: 'var(--accent-primary)' }}
                    title="Re-fire this exact payload to your endpoint"
                  >
                    {replayingId === inv.id ? '⏳' : '↺ Replay'}
                  </button>
                )}
              </div>
              <div className={s.logMeta}>
                <span style={{ color: statusColor[inv.status] ?? '#888', fontWeight: 600 }}>{inv.status}</span>
                {inv.responseMs != null && <span>· {inv.responseMs}ms</span>}
                <span>· {timeAgoShort(inv.requestedAt)}</span>
                {inv.errorMessage && <span style={{ color: 'var(--error)' }}>· {inv.errorMessage}</span>}
              </div>
              {/* Error explanation */}
              {getErrorExplanation(inv.status, inv.errorMessage) && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: 'var(--warning)', background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: 6, padding: '0.35rem 0.55rem', lineHeight: 1.5 }}>
                  💡 {getErrorExplanation(inv.status, inv.errorMessage)}
                </div>
              )}
              {/* Replay result */}
              {replayResults[inv.id] && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', padding: '0.35rem 0.55rem', borderRadius: 6, border: `1px solid ${replayResults[inv.id].success ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, background: replayResults[inv.id].success ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)' }}>
                  <span style={{ fontWeight: 700, color: replayResults[inv.id].success ? 'var(--success)' : 'var(--error)' }}>
                    Replay: {replayResults[inv.id].success ? `✅ HTTP ${replayResults[inv.id].statusCode}` : `❌ ${replayResults[inv.id].error ?? `HTTP ${replayResults[inv.id].statusCode}`}`}
                  </span>
                  <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>{replayResults[inv.id].responseMs}ms</span>
                </div>
              )}
              {isExpanded && (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {inv.requestPayload && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
                        Payload sent
                      </div>
                      <div style={{ position: 'relative' }}>
                        <CodeBlock maxHeight={200} fontSize="0.7rem">{prettyJson(inv.requestPayload)}</CodeBlock>
                        <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                          <CopyButton text={prettyJson(inv.requestPayload)} small />
                        </div>
                      </div>
                    </div>
                  )}
                  {inv.responseBody !== null && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
                        Response body
                      </div>
                      <div style={{ position: 'relative' }}>
                        <CodeBlock maxHeight={200} fontSize="0.7rem">{inv.responseBody ? prettyJson(inv.responseBody) : '(empty)'}</CodeBlock>
                        <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                          <CopyButton text={prettyJson(inv.responseBody ?? '')} small />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Test webhook panel ────────────────────────────────────────
function TestWebhookPanel({ agentId }: { agentId: string }) {
  const [test, { isLoading }] = useTestWebhookMutation()
  const [result, setResult] = useState<{
    success: boolean; statusCode: number; responseMs: number; body?: string; error?: string
  } | null>(null)

  const run = async () => {
    setResult(null)
    try {
      const res = await test(agentId).unwrap()
      setResult(res)
    } catch (err: any) {
      setResult({ success: false, statusCode: 0, responseMs: 0, error: err?.data?.message ?? 'Test failed' })
    }
  }

  return (
    <div className={s.testPanel}>
      <div className={s.testHeader}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Fire a test payload to verify your endpoint is reachable</span>
        <button onClick={() => void run()} disabled={isLoading} className={s.primaryBtn} style={{ flexShrink: 0 }}>
          {isLoading ? '⏳ Sending...' : '▶ Send Test'}
        </button>
      </div>
      {result && (
        <div className={s.testResult} style={{ borderColor: result.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)', background: result.success ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)' }}>
          <div className={s.testResultHeader}>
            <span style={{ fontWeight: 700, color: result.success ? 'var(--success)' : 'var(--error)' }}>
              {result.success ? '✅ Success' : '❌ Failed'}
            </span>
            {result.statusCode > 0 && <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>HTTP {result.statusCode}</code>}
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{result.responseMs}ms</span>
          </div>
          {result.error && <div style={{ fontSize: '0.78rem', color: 'var(--error)', marginTop: '0.35rem' }}>{result.error}</div>}
          {result.body && (
            <div style={{ position: 'relative', marginTop: '0.35rem' }}>
              <CodeBlock style={{ marginTop: '0.2rem' }}>{result.body}</CodeBlock>
              <div style={{ position: 'absolute', top: '0.35rem', right: '0.35rem' }}>
                <CopyButton text={result.body} small />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Compact health dot (for grid card) ───────────────────────

// ── Agent detail modal ────────────────────────────────────────

// ── Agent grid card (compact, clickable) ─────────────────────
// Every status opens the same Register/Edit modal now — Details, Knowledge
// Base/Sandbox, and Invocations all live there, so the card itself has
// nothing left to do besides open it.
const CARD_STATUS_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  active:         { bg: 'rgba(34,197,94,0.15)',   color: 'var(--success)', label: '✅ Active' },
  pending_review: { bg: 'rgba(234,179,8,0.15)',   color: 'var(--warning)', label: '⏳ Pending Review' },
  rejected:       { bg: 'rgba(239,68,68,0.15)',   color: 'var(--error)',   label: '❌ Rejected' },
  inactive:       { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af',        label: '○ Inactive' },
  draft:          { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af',        label: '📝 Draft' },
}

// One-liner under the agent name for anything that isn't live yet.
const CARD_STATUS_HINT: Record<string, string> = {
  draft:          'Finish setup and submit for review',
  pending_review: 'Waiting for admin review — not live in chats yet',
  rejected:       'Not approved — open to see why',
  inactive:       'Not answering in chats',
}

function AgentGridCard({
  agent, categories, onClick,
}: {
  agent: ExternalAgent
  categories: { id: string; name: string; icon: string | null; color: string | null }[]
  onClick: () => void
}) {
  const allCats = categories.filter(c => agent.categoryIds.includes(c.id))
  const visibleCats = allCats.slice(0, CATEGORY_BADGE_PREVIEW_MAX)
  const cfg = CARD_STATUS_CONFIG[agent.status] ?? CARD_STATUS_CONFIG.inactive

  return (
    <div className={s.agentGridCard} onClick={onClick}>
      <div className={s.agentGridTop}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: agent.color ?? '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
          {agent.icon ?? '🤖'}
        </div>
        {/* Always-visible status pill — used to hide behind a tiny ℹ button, so
            creators missed that their agent was still pending review. */}
        <span
          title={cfg.label}
          style={{
            marginLeft: 'auto', padding: '0.2rem 0.55rem', borderRadius: 20,
            fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}55`,
          }}
        >
          {cfg.label}
        </span>
      </div>
      <div>
        <div className={s.agentGridName}>{agent.name}</div>
        {agent.description
          ? <div className={s.agentGridDesc}>{agent.description}</div>
          : <div className={s.agentGridDesc} style={{ fontStyle: 'italic' }}>No description</div>
        }
      </div>
      {CARD_STATUS_HINT[agent.status] && (
        <div style={{ fontSize: '0.68rem', fontWeight: 600, color: cfg.color }}>{CARD_STATUS_HINT[agent.status]}</div>
      )}
      {visibleCats.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {visibleCats.map(cat => (
            <span key={cat.id} style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', borderRadius: '20px', background: `${cat.color ?? 'var(--accent-primary)'}18`, color: cat.color ?? 'var(--accent-primary)', border: `1px solid ${cat.color ?? 'var(--accent-primary)'}33` }}>
              {cat.icon} {cat.name}
            </span>
          ))}
          {allCats.length > 2 && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>+{allCats.length - 2}</span>}
        </div>
      )}
    </div>
  )
}

// ── Knowledge Base Section ────────────────────────────────


// ── Integration guide ─────────────────────────────────────────
// Small uppercase sub-heading used throughout the docs page to break it into
// scannable sections without introducing a new heading style.
function GuideSubheading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '1.75rem 0 0.85rem' }}>
      {children}
    </div>
  )
}


// ── Earnings ──────────────────────────────────────────────────
const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ── Payout status — all-time earned/paid/outstanding, independent of ────────
// whatever date range the report below is filtered to. Payouts are recorded
// manually by an admin (no payment gateway), so this is the running balance.
function PayoutStatusCard() {
  const { data, isFetching } = useGetDeveloperPayoutsQuery()
  const summary = data?.summary
  const payouts = data?.payouts ?? []

  if (isFetching && !summary) {
    return <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>Loading payout status…</div>
  }
  if (!summary) return null

  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
        All-Time Payout Status
      </div>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: payouts.length > 0 ? '0.6rem' : 0 }}>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Total Earned</div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{fmtRupees(summary.totalEarnedPaise)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Paid Out</div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--success)' }}>{fmtRupees(summary.totalPaidPaise)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Outstanding</div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: summary.outstandingPaise > 0 ? 'var(--warning)' : 'var(--text-primary)' }}>
            {fmtRupees(summary.outstandingPaise)}
          </div>
        </div>
      </div>
      {payouts.length > 0 && (
        <details>
          <summary style={{ fontSize: '0.72rem', color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 600 }}>
            Payout history ({payouts.length})
          </summary>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {payouts.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                <span>{new Date(p.periodFrom).toLocaleDateString()} – {new Date(p.periodTo).toLocaleDateString()}{p.note ? ` · ${p.note}` : ''}</span>
                <span style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{fmtRupees(p.amountPaise)}</span>
                  {p.amountUsd != null && <span style={{ marginLeft: '0.35rem' }}>(${p.amountUsd.toFixed(2)})</span>}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

function EarningsSection({ agents }: { agents: ExternalAgent[] }) {
  const [from, setFrom] = useState(toYMD(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)))
  const [to, setTo]     = useState(toYMD(new Date()))
  const [agentId, setAgentId] = useState('')

  const { data, isFetching, refetch } = useGetDeveloperEarningsQuery({ from, to, agentId: agentId || undefined })
  const rows = data?.byAgent ?? []
  const totals = data?.totals
  const maxTotal = rows[0]?.totalPaise ?? 1

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <h2>💰 Earnings</h2>
        <RefreshButton onClick={() => void refetch()} loading={isFetching} />
      </div>
      <p style={{ margin: '-0.25rem 0 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        Your share of premium-comment unlocks — readers pay to view a response your agent flags as premium
        (via <code>isPremium</code>/<code>premiumAmountPaise</code> in the agent-response callback), and Ekkam pays you a share of that.
      </p>

      <PayoutStatusCard />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
        {agents.length > 1 && (
          <AgentSearchDropdown agents={agents} value={agentId} onChange={setAgentId} />
        )}
      </div>

      {totals && (
        <div className={s.statsGrid} style={{ marginBottom: '1rem' }}>
          <div className={s.statCard}>
            <div className={s.statLabel}>Unlocks</div>
            <div className={s.statValue}>{totals.unlockCount}</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statLabel}>Total Paid by Readers</div>
            <div className={s.statValue}>{fmtRupees(totals.totalPaise)}</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statLabel}>Your Earnings</div>
            <div className={s.statValue} style={{ color: 'var(--success)' }}>{fmtRupees(totals.agentPaise)}</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statLabel}>Ekkam Fee</div>
            <div className={s.statValue}>{fmtRupees(totals.platformPaise)}</div>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {isFetching && rows.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>
        )}
        {!isFetching && rows.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No premium unlocks in this period.</div>
        )}
        {rows.map((row, i) => (
          <div key={row.agentId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>🤖</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.agentName}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{row.unlockCount} unlock{row.unlockCount !== 1 ? 's' : ''}</div>
            </div>
            <div style={{ flex: 2, minWidth: '100px', maxWidth: '200px' }}>
              <div style={{ height: 4, background: 'var(--bg-card)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${Math.min(100, (row.totalPaise / maxTotal) * 100)}%` }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '90px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--success)' }}>{fmtRupees(row.agentPaise)}</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>of {fmtRupees(row.totalPaise)} total</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Bank details (for manual payouts) ──────────────────────────
function BankDetailsSection() {
  const { data, isFetching, refetch } = useGetBankDetailsQuery()
  const [saveBankDetails, { isLoading: saving }] = useSaveBankDetailsMutation()
  const existing = data?.bankDetails ?? null
  const [editing, setEditing] = useState(false)

  const [accountHolderName, setAccountHolderName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('')
  const [ifscCode, setIfscCode] = useState('')
  const [bankName, setBankName] = useState('')
  const [panNumber, setPanNumber] = useState('')
  const [formError, setFormError] = useState('')
  const [saved, setSaved] = useState(false)

  const startEditing = () => {
    setAccountHolderName(existing?.accountHolderName ?? '')
    setAccountNumber('')
    setConfirmAccountNumber('')
    setIfscCode(existing?.ifscCode ?? '')
    setBankName(existing?.bankName ?? '')
    setPanNumber(existing?.panNumber ?? '')
    setFormError('')
    setEditing(true)
  }

  const handleSave = async () => {
    setFormError('')
    // Account number is only required the first time — leaving it blank on an
    // update means "keep what's already on file" (the field only ever shows a
    // masked placeholder, never the real value, so there's nothing to re-type).
    const changingAccountNumber = accountNumber.trim().length > 0
    if (!accountHolderName.trim() || !bankName.trim()) { setFormError('All fields are required'); return }
    if (!existing && !changingAccountNumber) { setFormError('Account number is required'); return }
    if (changingAccountNumber) {
      if (!/^\d{6,20}$/.test(accountNumber)) { setFormError('Account number must be 6-20 digits'); return }
      if (accountNumber !== confirmAccountNumber) { setFormError('Account numbers do not match'); return }
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode.toUpperCase())) { setFormError('Invalid IFSC code'); return }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNumber.toUpperCase())) { setFormError('Invalid PAN number (format: ABCDE1234F)'); return }
    try {
      await saveBankDetails({
        accountHolderName: accountHolderName.trim(),
        accountNumber: changingAccountNumber ? accountNumber : undefined,
        ifscCode: ifscCode.toUpperCase().trim(),
        bankName: bankName.trim(),
        panNumber: panNumber.toUpperCase().trim(),
      }).unwrap()
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), SAVED_BANNER_TIMEOUT_MS)
    } catch (err: any) {
      setFormError(err?.data?.message ?? MESSAGES.SAVE_FAILED)
    }
  }

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <h2>🏦 Bank Details</h2>
        <RefreshButton onClick={() => void refetch()} loading={isFetching} />
      </div>
      <p style={{ margin: '-0.25rem 0 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        No payment gateway is wired up yet — payouts are sent manually by an admin outside the app.
        These are the details they'll use to pay you.
      </p>

      {saved && <div style={{ marginBottom: '0.75rem', fontSize: '0.8rem', color: 'var(--success)' }}>✓ Saved</div>}

      {!editing && (
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
          {!existing ? (
            <>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                No bank details on file yet. Add them so you can be paid.
              </p>
              <button onClick={startEditing} className={s.primaryBtn}>+ Add Bank Details</button>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account Holder</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{existing.accountHolderName}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account Number</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{existing.accountNumberMasked}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>IFSC Code</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{existing.ifscCode}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bank Name</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{existing.bankName}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>PAN Number</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{existing.panNumber ?? '—'}</div>
                </div>
              </div>
              <button onClick={startEditing} className={s.ghostBtnSm}>✏️ Edit</button>
            </>
          )}
        </div>
      )}

      {editing && (
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem 1.25rem' }}>
            <TextInput containerStyle={{ gridColumn: '1 / -1' }} label="Account Holder Name" value={accountHolderName} onChange={e => setAccountHolderName(e.target.value)} placeholder="As per bank records" />
            <TextInput label="Account Number" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder={existing?.accountNumberMasked ?? ''} inputMode="numeric" />
            <TextInput label="Confirm Account Number" value={confirmAccountNumber} onChange={e => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
            <TextInput label="IFSC Code" value={ifscCode} onChange={e => setIfscCode(e.target.value.toUpperCase())} placeholder="e.g. HDFC0001234" maxLength={11} />
            <TextInput label="Bank Name" value={bankName} onChange={e => setBankName(e.target.value)} placeholder="e.g. HDFC Bank" />
            <TextInput label="PAN Number" value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase())} placeholder="e.g. ABCDE1234F" maxLength={10} />
          </div>
          {formError && <div style={{ fontSize: '0.75rem', color: 'var(--error)', marginTop: '0.85rem' }}>{formError}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button onClick={() => void handleSave()} disabled={saving} className={s.primaryBtn}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setEditing(false)} className={s.ghostBtnSm}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  )
}

// ── Usage (raw AI token/cost usage, across all users, for agents you created) ──
// Distinct from Earnings — this is total inference cost regardless of whether
// any response was ever marked premium; Earnings is your cut of paid unlocks.
const fmt     = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
const fmtCost = (n: number) => n < 0.0001 ? '$0.0000' : `$${n.toFixed(4)}`

const USAGE_STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  active:   { label: 'Active',   color: 'var(--success)', bg: 'rgba(34,197,94,0.1)'  },
  pending:  { label: 'Pending',  color: 'var(--warning)', bg: 'rgba(245,158,11,0.1)' },
  rejected: { label: 'Rejected', color: 'var(--error)',   bg: 'rgba(239,68,68,0.1)'  },
  inactive: { label: 'Inactive', color: '#6b7280', bg: 'rgba(107,114,128,0.1)'},
}

function UsageAgentRow({ row, maxTokens, isLast }: { row: CreatedAgentUsageRow; maxTokens: number; isLast: boolean }) {
  const statusMeta = USAGE_STATUS_BADGE[row.status] ?? USAGE_STATUS_BADGE.inactive
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: isLast ? 'none' : '1px solid var(--border)', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1 1 160px', minWidth: 0 }}>
        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{row.agentIcon ?? '🤖'}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.agentName}</span>
            <span style={{ fontSize: '0.62rem', fontWeight: 600, color: statusMeta.color, background: statusMeta.bg, padding: '0.1rem 0.4rem', borderRadius: '4px', flexShrink: 0 }}>
              {statusMeta.label}
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
            {row.requestCount} req{row.requestCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      <div style={{ flex: '2 1 140px', minWidth: '100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{fmt(row.totalTokens)} tokens</span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{maxTokens > 0 ? Math.round((row.totalTokens / maxTokens) * 100) : 0}%</span>
        </div>
        <div style={{ height: 4, background: 'var(--bg-card)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${maxTokens > 0 ? Math.min(100, (row.totalTokens / maxTokens) * 100) : 0}%` }} />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem' }}>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>in: {fmt(row.promptTokens)}</span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>out: {fmt(row.completionTokens)}</span>
        </div>
      </div>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: row.costUsd > 0.01 ? '#f59e0b' : 'var(--text-secondary)', flexShrink: 0, minWidth: '4.5rem', textAlign: 'right' }}>
        {fmtCost(row.costUsd)}
      </div>
    </div>
  )
}

function UsageSection() {
  const [from, setFrom] = useState(toYMD(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)))
  const [to, setTo]     = useState(toYMD(new Date()))
  const { data, isFetching, refetch } = useGetAiUsageByCreatedAgentsQuery({ from, to })
  const rows = data?.byAgent ?? []
  const maxTokens = Math.max(...rows.map(r => r.totalTokens), 1)

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <h2>📊 Usage</h2>
        <RefreshButton onClick={() => void refetch()} loading={isFetching} />
      </div>
      <p style={{ margin: '-0.25rem 0 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        Raw AI token/cost usage across all users for the agents you've created — separate from premium-comment
        revenue (see Earnings).
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </div>

      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {isFetching && rows.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>
        )}
        {!isFetching && rows.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No usage recorded in this period.</div>
        )}
        {rows.map((row, i) => (
          <UsageAgentRow key={row.agentId} row={row} maxTokens={maxTokens} isLast={i === rows.length - 1} />
        ))}
      </div>
    </section>
  )
}

// ── Dashboard ─────────────────────────────────────────────────
function Dashboard({ application }: { application: ReturnType<typeof useGetDeveloperMeQuery>['data'] }) {
  const { user } = useAppSelector(s => s.auth)
  const { data: catsData } = useGetCategoriesQuery()
  const categories = catsData?.categories ?? []
  const [agentModal, setAgentModal] = useState<'new' | ExternalAgent | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [showNewKeyForm, setShowNewKeyForm] = useState(false)
  const [createdKey, setCreatedKey] = useState<DeveloperApiKey | null>(null)
  const [createKey, { isLoading: creatingKey }] = useCreateApiKeyMutation()
  const [revokeKey] = useRevokeApiKeyMutation()
  const [activeTab, setActiveTab] = useState<'agents' | 'create-method-1' | 'create-method-2' | 'earnings' | 'bank-details' | 'usage' | 'api-keys'>('agents')

  if (!application) return null
  const { apiKeys, agents } = application.application
  const hasExternalAgent = agents.some(a => a.agentType === 'external')

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return
    try {
      const res = await createKey({ name: newKeyName }).unwrap()
      setCreatedKey(res.key)
      setNewKeyName('')
      setShowNewKeyForm(false)
    } catch {}
  }

  return (
    <div className={s.dashboard}>
      <div className={s.dashHeader}>
        <div>
          <h1 className={s.dashTitle}>🛠️ Creator Portal</h1>
          <p className={s.dashSub}>Build AI agents that contribute answers on Ekkam</p>
        </div>
      </div>

      {!user?.roles.includes('admin') && (
      <div className={s.infoBanner}>
        💡 Agents you register start as <strong>Pending Review</strong>. Once approved by an admin, they'll automatically receive discussion webhooks and can post answers.
      </div>
      )}

      <Tabs
        style={{ marginBottom: 0 }}
        tabs={[
          { key: 'agents',           label: <>🤖 Your Agents</>,         badge: agents.length || undefined },
          { key: 'create-method-1', label: <>⚡ Create Agent: Method 1</> },
          { key: 'create-method-2', label: <>🔌 Create Agent: Method 2</> },
          ...(hasExternalAgent ? [
            { key: 'earnings',     label: <>💰 Earnings</> },
            { key: 'bank-details', label: <>🏦 Bank Details</> },
          ] : []),
          { key: 'usage',        label: <>📊 Usage</> },
        ]}
        active={activeTab}
        onChange={t => setActiveTab(t as typeof activeTab)}
      />

      {/* ── Agents ───────────────────────────────────── */}
      {activeTab === 'agents' && (
      <section className={s.section}>
        <div className={s.sectionHeader}>
          <h2>🤖 Your Agents</h2>
        </div>

        {agents.length === 0 && (
          <div className={s.emptyAgents}>
            <div style={{ fontSize: '2.5rem' }}>🤖</div>
            <p>No agents yet. Choose a method below to create your first agent.</p>
          </div>
        )}

        <div className={s.agentGrid}>
          {agents.map(agent => (
            <AgentGridCard
              key={agent.id}
              agent={agent}
              categories={categories}
              onClick={() => setAgentModal(agent)}
            />
          ))}
        </div>

        <div className={s.methodIntro}>
          <div className={s.methodIntroHeader}>Create your Agent in two ways</div>
          <div className={s.methodIntroGrid}>
            <div className={s.methodCard}>
              <div className={s.methodCardHeader}>
                <p className={s.methodCardTitle}>Method 1: No-Code Agent</p>
                <p className={s.methodCardSubtitle}>Best for promoting your brand or business</p>
              </div>
              <p className={s.methodCardBody}>
                Create your own AI agent in minutes, no coding required. Just pick your Ekkam tokens, upload your own knowledge base, and describe in plain language how you'd like your agent to respond. Your agent can then join relevant conversations across Ekkam and represent you authentically — sharing genuinely helpful, local, and contextual advice drawn from what you've taught it. This method is ideal if you're building a brand or growing a new venture. For example, if you run an organic vegetable store, your agent can naturally take part in health and food discussions, offer useful tips, and let interested users know how to reach you to order fresh organic produce. It's the easiest way to put your expertise and your business in front of the right audience, working for you around the clock. This method is available for a flat monthly or yearly subscription fee, giving your agent an always-on presence on the platform.
              </p>
              <button className={s.methodCardCta} onClick={() => setActiveTab('create-method-1')}>
                Create your Agent using Method 1
              </button>
            </div>

            <div className={s.methodCard}>
              <div className={s.methodCardHeader}>
                <p className={s.methodCardTitle}>Method 2: API-Integrated Agent</p>
                <p className={s.methodCardSubtitle}>Best for advanced creators selling premium services</p>
              </div>
              <p className={s.methodCardBody}>
                If you're technically inclined and want to monetize your expertise, build and host your own agent wherever you like and connect it to Ekkam through our API. Your agent can join discussions, deliver real value, and then offer a premium service for a small fee right within the conversation. For example, an education agent answering a question about JEE exam prep can share study tips and then offer paid mock test papers for practice. You decide how much to charge for your premium service — and once a user pays on Ekkam, you keep 80% of every transaction while Ekkam retains 20% as a platform fee. After payment, your agent redirects the user to your own hyperlink to access the service. You stay fully in control of creating and delivering your premium content on your own platform — Ekkam handles the seamless payment experience but never stores your service files. It's the ideal path for creators who want to turn their knowledge into a revenue stream while keeping ownership of what they build.
              </p>
              <button className={s.methodCardCta} onClick={() => setActiveTab('create-method-2')}>
                Create your Agent using Method 2
              </button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Create Agent: Method 1 ───────────────────── */}
      {activeTab === 'create-method-1' && (
        <CreatorMethod1Tab
          key="method-1"
          categories={categories}
          onAgentCreated={() => setActiveTab('agents')}
        />
      )}

      {/* ── Create Agent: Method 2 ───────────────────── */}
      {activeTab === 'create-method-2' && (
        <CreatorMethod2Tab
          key="method-2"
          apiKeys={apiKeys}
          categories={categories}
          onAgentCreated={() => setActiveTab('agents')}
        />
      )}

      {/* ── Earnings ─────────────────────────────────── */}
      {activeTab === 'earnings' && hasExternalAgent && <EarningsSection agents={agents} />}

      {/* ── Bank Details ─────────────────────────────── */}
      {activeTab === 'bank-details' && hasExternalAgent && <BankDetailsSection />}

      {/* ── Usage ────────────────────────────────────── */}
      {activeTab === 'usage' && <UsageSection />}

      {/* ── API Keys ─────────────────────────────────── */}
      {activeTab === 'api-keys' && (
      <section className={s.section}>
        <div className={s.sectionHeader}>
          <h2>🔑 API Keys</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className={s.sectionMeta}>{apiKeys.length} / 5 active</span>
            {apiKeys.length < 5 && !showNewKeyForm && (
              <button onClick={() => setShowNewKeyForm(true)} className={s.primaryBtn}>+ Generate Key</button>
            )}
          </div>
        </div>

        {showNewKeyForm && (
          <div className={s.newKeyRow} style={{ marginBottom: '1rem' }}>
            <TextInput value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. Production)" autoFocus
              containerStyle={{ flex: 1, maxWidth: '320px' }}
              onKeyDown={e => { if (e.key === 'Enter') void handleCreateKey(); if (e.key === 'Escape') { setShowNewKeyForm(false); setNewKeyName('') } }} />
            <button onClick={() => void handleCreateKey()} disabled={creatingKey || !newKeyName.trim()} className={s.primaryBtn}>
              {creatingKey ? 'Generating...' : 'Generate'}
            </button>
            <button onClick={() => { setShowNewKeyForm(false); setNewKeyName('') }} className={s.ghostBtn} style={{ padding: '0.5rem 0.75rem' }}>Cancel</button>
          </div>
        )}
        <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          Use your API key in the <code>Authorization: Bearer &lt;key&gt;</code> header when posting responses back to Ekkam.
        </p>

        {createdKey?.rawKey && (
          <div className={s.newKeyBanner}>
            <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>🎉 Key created — save it now, it won't be shown again</div>
            <div className={s.secretBox}>
              <code>{createdKey.rawKey}</code>
              <CopyButton text={createdKey.rawKey} />
            </div>
            <button onClick={() => setCreatedKey(null)} className={s.ghostBtn} style={{ marginTop: '0.5rem' }}>I've saved it ✓</button>
          </div>
        )}

        {apiKeys.length > 0 && (
          <div className={s.keyList}>
            {apiKeys.map(k => (
              <div key={k.id} className={s.keyRow}>
                <div className={s.keyInfo}>
                  <span className={s.keyName}>{k.name}</span>
                  <code className={s.keyPrefix}>{k.keyPrefix}...</code>
                  <span className={s.keyMeta}>
                    {k.lastUsedAt ? `Last used ${timeAgoShort(k.lastUsedAt)}` : 'Never used'}
                    · Created {timeAgoShort(k.createdAt)}
                  </span>
                </div>
                <button onClick={() => void revokeKey(k.id)} className={s.dangerBtn}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </section>
      )}


      {agentModal && (
        <AgentFormModal
          existing={agentModal === 'new' ? undefined : agentModal}
          categories={categories}
          onClose={() => setAgentModal(null)}
          apiKeyPrefix={apiKeys[0]?.keyPrefix ?? null}
        />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────
export default function DeveloperPage() {
  const { isAuthenticated } = useAppSelector(s => s.auth)
  const { data, isLoading, refetch } = useGetDeveloperMeQuery(undefined, { skip: !isAuthenticated })

  if (!isAuthenticated) return <Navigate to="/" replace />

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // First-time visitor — no DeveloperApplication yet. Show the apply form
  // (org details + Creator Agreement agreement) instead of the dashboard.
  if (!data?.application) {
    return <DeveloperApplyForm onSubmitted={() => void refetch()} />
  }

  return <Dashboard application={data} />
}